// @workflow assistant.ui-tools
/**
 * Prepare, don't refuse (Khalid, 2026-10-13): before focus_tags or color_tags acts, it gets the 3D
 * model on screen itself. The tag tools (tagTools.js) need less, only the facility's page, loaded
 * (PREPARE_NEED.FACILITY): the same facility hop, with no 3D pane and no 3D model required. It opens the facility when another one was asked for, opens a 3D pane
 * beside the view the user has (or shows a hidden one; with focus_tags' `target: current` it switches
 * the view targetPaneSlot picks to 3D instead), and waits, bounded and cancellable, until
 * the model, its tree, the main extension and the facility's tags are loaded. It stops only when
 * there is truly no way, and says why.
 *
 * `nextPrepareStep` is the whole decision, a pure function of one state snapshot; `prepareViewer`
 * is the loop that reads a snapshot, acts at most once per kind of step, and waits.
 *
 * Host contract (`host.viewer`, see services/viewerHost.js):
 *   readState(facilityId|null) → snapshot (fields listed at `nextPrepareStep`)
 *   leaveViewerLayout(facilityId) → Promise<OPEN_FACILITY value>   access check, then the facilities list
 *   openFacility(facilityId)   → Promise<OPEN_FACILITY value>
 *   open3dPane()               → boolean (false: no free pane)
 *   show3dPane()               → void
 *   switchPaneTo3d(slot)       → boolean   the user's own "switch this view to 3D" on that slot
 *                                          (false: the viewer page is gone)
 *   openPageViewer()           → boolean   the page's own 3D panel: open it on 3D and start loading
 *                                          the model there (false: the page no longer has one); the
 *                                          load's progress reads as pageViewerLoading / pageViewerFailed
 * `host.signal` (AbortSignal) stops the wait; `host.progress(text|null)` shows the line;
 * `host.clock` ({ now, sleep(ms, signal) }) is injectable for tests.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { notAvailable, failed } from './uiToolContract.js';
import { PANE_TARGET, VIEWER_DETAIL, VIEWER_PROGRESS, VIEWER_WAIT, sameId } from './viewerContract.js';

export const PREPARE_STEP = Object.freeze({
  READY: 'ready',
  WAIT: 'wait',
  /** Go to the facilities list first, so the viewer layout unmounts and loads the next facility fresh. */
  LEAVE_VIEWER_LAYOUT: 'leave-viewer-layout',
  NAVIGATE: 'navigate',
  OPEN_PANE: 'open-pane',
  SHOW_PANE: 'show-pane',
  /** Switch the view targetPaneSlot picked to 3D (`target: current`), instead of opening one beside it. */
  SWITCH_PANE: 'switch-pane',
  /** The page has its own 3D panel (Registers page, task board): use it in place, never navigate. */
  OPEN_PAGE_VIEWER: 'open-page-viewer',
  STOP: 'stop',
});

/** How much a tool needs before it acts: the 3D model on screen, or only the facility's page. */
export const PREPARE_NEED = Object.freeze({ MODEL_3D: 'model-3d', FACILITY: 'facility' });

/** Where the 3D view stands in the page's panes. */
export const PANE_3D = Object.freeze({ VISIBLE: 'visible', HIDDEN: 'hidden', NONE: 'none' });

/** What `openFacility` answers. */
export const OPEN_FACILITY = Object.freeze({ OPENED: 'opened', DENIED: 'denied', FAILED: 'failed' });

/** The pane type of the 3D viewer (utils/viewerNavigationUtils.js VIEWER_TYPES.MODEL_3D). */
export const PANE_TYPE_3D = '3d';

/**
 * @param {Array<{ type: string, visible: boolean }>} panes the page's view slots
 * @returns {string} a PANE_3D value
 */
export function pane3dState(panes) {
  const panes3d = (panes || []).filter((pane) => pane && pane.type === PANE_TYPE_3D);
  if (panes3d.some((pane) => pane.visible)) return PANE_3D.VISIBLE;
  return panes3d.length ? PANE_3D.HIDDEN : PANE_3D.NONE;
}

const stop = (result) => ({ kind: PREPARE_STEP.STOP, result });
const wait = (progress) => ({ kind: PREPARE_STEP.WAIT, progress });

const isModelOnScreen = (s) => !!(s.hasViewer && s.hasModel && s.hasInstanceTree && s.hasMainExtension && s.isOnScreen);

/**
 * Off the target facility's viewer page: go there. The app loads a facility when the viewer layout
 * MOUNTS (layouts/view.vue `created` → INITIALIZE_MAIN_DATA); from a page already in that layout a
 * route change would keep the old facility's data, and the app's own switcher reloads the page,
 * which would end the reply. So another facility from the viewer layout goes through the
 * facilities list: leave, wait until the list is current and the viewer layout has unmounted,
 * then open the facility, whose layout mounts fresh.
 */
function toFacilityStep(state, { target, here, done, opening, hereLine }) {
  if (done.navigated) return stop(notAvailable(VIEWER_DETAIL.LEFT_PAGE));
  const navigate = { kind: PREPARE_STEP.NAVIGATE, facilityId: target, switched: !here, progress: here ? hereLine : opening };
  if (here) return navigate;
  if (done.leftLayout) {
    // The list route is current once the push resolved; anywhere else means the user moved on.
    if (!state.onFacilitiesList) return stop(notAvailable(VIEWER_DETAIL.LEFT_PAGE));
    return state.viewerLayoutMounted ? wait(opening) : navigate;
  }
  if (state.layout === state.targetLayout) return { kind: PREPARE_STEP.LEAVE_VIEWER_LAYOUT, facilityId: target, progress: opening };
  return navigate;
}

/**
 * The view `target: current` uses (MainView keeps no active view), Khalid's rule of 2026-10-17, in order:
 *  1. a visible view that already shows `type`: it (the first in slot order when several);
 *  2. else, with only one view open: that one;
 *  3. else the first open view in slot order (top-left, bottom-left, top-right, bottom-right,
 *     MainView's slots 1 to 4).
 * "Open" is visible. Null when no view is visible.
 * @param {Array<{ slot: number, type: string, visible: boolean }>} panes the page's view slots
 * @param {string} type the pane type wanted (PANE_TYPE_3D)
 * @returns {number|null}
 */
export function targetPaneSlot(panes, type) {
  const open = (panes || []).filter((pane) => pane && pane.visible).sort((a, b) => a.slot - b.slot);
  const sameType = open.find((pane) => pane.type === type);
  return (sameType || open[0] || {}).slot ?? null;
}

/** No 3D view on screen: switch the view targetPaneSlot picks (target current), else show the hidden one or open one. */
function pane3dStep(state, paneTarget) {
  const progress = VIEWER_PROGRESS.OPENING_MODEL;
  if (paneTarget === PANE_TARGET.CURRENT && state.currentSlot) return { kind: PREPARE_STEP.SWITCH_PANE, slot: state.currentSlot, progress };
  return { kind: state.pane3d === PANE_3D.HIDDEN ? PREPARE_STEP.SHOW_PANE : PREPARE_STEP.OPEN_PANE, progress };
}

/**
 * A page with its own 3D panel for this facility (platform/viewer/pageViewer.js): open that panel
 * and wait for its model. MainView's pane slots are not read here; they keep MainView's last state
 * after it unmounted.
 */
function pageViewerStep(state, done) {
  if (!state.facilityLoaded) return wait(VIEWER_PROGRESS.OPENING_MODEL);
  if (!state.hasModel3d) return stop(notAvailable(VIEWER_DETAIL.NO_MODEL));
  if (!done.pageViewer) return { kind: PREPARE_STEP.OPEN_PAGE_VIEWER, progress: VIEWER_PROGRESS.OPENING_MODEL };
  if (state.pageViewerFailed) return stop(failed(VIEWER_DETAIL.PAGE_LOAD_FAILED));
  if (!state.pageViewer3dOpen) return stop(notAvailable(VIEWER_DETAIL.CLOSED));
  return wait(VIEWER_PROGRESS.OPENING_MODEL);
}

/**
 * The next thing to do. `done` records what this request already did, so nothing is done twice
 * and an undone step reads as the user's choice (they left, or closed the pane).
 * @param {{
 *   routeFacilityId: string|null,   facility in the URL (null off facility pages)
 *   layout: string|null, targetLayout: string|null,   layout of this page and of the facility's viewer page
 *   onViewerPage: boolean, hasMainApp: boolean,   on the facility viewer page, and its panes can be driven
 *   onFacilitiesList: boolean, viewerLayoutMounted: boolean,   the facilities list is current; the viewer layout is in the page
 *   facilityLoaded: boolean, hasModel3d: boolean,   facility data and tags loaded; it has a 3D model
 *   pane3d: string,   a PANE_3D value
 *   currentSlot: number|null,   the view target current uses (targetPaneSlot)
 *   hasViewer: boolean, hasModel: boolean, hasInstanceTree: boolean, hasMainExtension: boolean,
 *   isOnScreen: boolean,   the 3D canvas is attached and visible
 *   facilityName: string|null,   the name of the facility to open, for the progress line
 *   pageViewerFacilityId: string|null, pageViewer3dOpen: boolean,   the page's own 3D panel: its facility (null: none), open on 3D
 *   pageViewerLoading: boolean, pageViewerFailed: boolean,   the load AVA started there is running / failed
 * }} state
 * @param {{ facilityId?: string|null, done?: { leftLayout?: boolean, navigated?: boolean, switched?: boolean, pane?: boolean, pageViewer?: boolean }, need?: string, paneTarget?: string }} [request]
 *        `need`: a PREPARE_NEED value (default MODEL_3D); `paneTarget`: a PANE_TARGET value (default NEW),
 *        used only when no 3D view is on screen (a visible one is used where it is)
 * @returns {{ kind: string, progress?: string, result?: object, facilityId?: string, switched?: boolean, slot?: number }}
 */
export function nextPrepareStep(state, { facilityId = null, done = {}, need = PREPARE_NEED.MODEL_3D, paneTarget = PANE_TARGET.NEW } = {}) {
  const target = facilityId || state.routeFacilityId;
  if (!target) return stop(notAvailable(VIEWER_DETAIL.NO_FACILITY));
  const here = sameId(state.routeFacilityId, target);
  const modelNeeded = need !== PREPARE_NEED.FACILITY;
  // A 3D model already on screen in this facility is used where it is (a dashboard's 3D view too),
  // once a page panel AVA opened finished loading (its earlier model can be on screen meanwhile).
  if (modelNeeded && here && state.facilityLoaded && !state.pageViewerLoading && isModelOnScreen(state)) return { kind: PREPARE_STEP.READY };
  const opening = VIEWER_PROGRESS.openingFacility(state.facilityName);
  const hereLine = modelNeeded ? VIEWER_PROGRESS.OPENING_MODEL : opening;
  if (modelNeeded && here && sameId(state.pageViewerFacilityId, target)) return pageViewerStep(state, done);
  if (!here || !state.onViewerPage) return toFacilityStep(state, { target, here, done, opening, hereLine });
  const loadingLine = done.switched ? opening : hereLine;
  // "No 3D model" is only known once the facility finished loading; until then it reads false.
  if (!state.facilityLoaded || !state.hasMainApp) return wait(loadingLine);
  if (!modelNeeded) return { kind: PREPARE_STEP.READY };
  if (!state.hasModel3d) return stop(notAvailable(VIEWER_DETAIL.NO_MODEL));
  if (state.pane3d !== PANE_3D.VISIBLE) {
    if (done.pane) return stop(notAvailable(VIEWER_DETAIL.CLOSED));
    return pane3dStep(state, paneTarget);
  }
  return wait(VIEWER_PROGRESS.OPENING_MODEL);
}

/** A timer that also ends when `signal` aborts, so a stopped request never waits on. */
export function abortableSleep(ms, signal) {
  return new Promise((resolve) => {
    if (signal && signal.aborted) {
      resolve();
      return;
    }
    let timer = null;
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    timer = setTimeout(() => {
      if (signal) signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    if (signal) signal.addEventListener('abort', onAbort, { once: true });
  });
}

export const SYSTEM_CLOCK = Object.freeze({ now: () => Date.now(), sleep: abortableSleep });

/** Calls `progress` only when the line changes (so a request that was ready at once shows none). */
export function progressReporter(progress) {
  let last = null;
  return (text) => {
    const line = text || null;
    if (line === last || typeof progress !== 'function') return;
    last = line;
    progress(line);
  };
}

/** What a step leaves behind in `done`, so it is never done twice. */
function doneAfter(step) {
  if (step.kind === PREPARE_STEP.LEAVE_VIEWER_LAYOUT) return { leftLayout: true };
  if (step.kind === PREPARE_STEP.NAVIGATE) return { navigated: true, switched: step.switched };
  if (step.kind === PREPARE_STEP.OPEN_PANE || step.kind === PREPARE_STEP.SHOW_PANE || step.kind === PREPARE_STEP.SWITCH_PANE) return { pane: true };
  if (step.kind === PREPARE_STEP.OPEN_PAGE_VIEWER) return { pageViewer: true };
  return {};
}

/** Does a NAVIGATE / OPEN_PANE / SHOW_PANE / SWITCH_PANE / OPEN_PAGE_VIEWER step; returns a result to stop with, or null. */
async function act(step, viewer) {
  if (step.kind === PREPARE_STEP.LEAVE_VIEWER_LAYOUT) {
    const left = await viewer.leaveViewerLayout(step.facilityId);
    if (left === OPEN_FACILITY.DENIED) return notAvailable(VIEWER_DETAIL.NO_ACCESS);
    return left === OPEN_FACILITY.OPENED ? null : failed(VIEWER_DETAIL.LIST_NOT_OPENED);
  }
  if (step.kind === PREPARE_STEP.NAVIGATE) {
    const opened = await viewer.openFacility(step.facilityId);
    if (opened === OPEN_FACILITY.DENIED) return notAvailable(VIEWER_DETAIL.NO_ACCESS);
    return opened === OPEN_FACILITY.OPENED ? null : failed(VIEWER_DETAIL.NOT_OPENED);
  }
  if (step.kind === PREPARE_STEP.OPEN_PANE) return viewer.open3dPane() ? null : notAvailable(VIEWER_DETAIL.NO_ROOM);
  if (step.kind === PREPARE_STEP.SWITCH_PANE) return viewer.switchPaneTo3d(step.slot) ? null : notAvailable(VIEWER_DETAIL.LEFT_PAGE);
  // Starts the load and returns; the poll below waits on readiness, so stop and the time limit still hold.
  if (step.kind === PREPARE_STEP.OPEN_PAGE_VIEWER) return viewer.openPageViewer() ? null : notAvailable(VIEWER_DETAIL.CLOSED);
  if (step.kind === PREPARE_STEP.SHOW_PANE) viewer.show3dPane();
  return null;
}

/**
 * The wait ends at the named limit, or sooner when the request expires first: while the reply is
 * paused no stream is open, so the UI would not hear that the server gave up.
 */
export function deadlineOf(now, expiresAt, timeoutMs = VIEWER_WAIT.TIMEOUT_MS) {
  const limit = now + timeoutMs;
  const expires = typeof expiresAt === 'string' ? Date.parse(expiresAt) : NaN;
  return Number.isFinite(expires) ? Math.min(limit, expires) : limit;
}

/**
 * Gets the 3D model on screen for `facilityId` (null: the facility in the URL), or with
 * PREPARE_NEED.FACILITY only that facility's page, loaded.
 * `host.expiresAt` (the request's ISO expiry), when given, can end the wait sooner.
 * @param {object} host
 * @param {string|null|undefined} facilityId
 * @param {string} [need] a PREPARE_NEED value (default MODEL_3D)
 * @param {string} [paneTarget] a PANE_TARGET value (default NEW): where a missing 3D view goes
 * @returns {Promise<null|object>} null when ready, else the not_available / failed result to answer
 */
export async function prepareViewer(host, facilityId, need = PREPARE_NEED.MODEL_3D, paneTarget = PANE_TARGET.NEW) {
  const viewer = host && host.viewer;
  if (!viewer) return notAvailable(VIEWER_DETAIL.NO_VIEWER);
  const signal = host.signal || null;
  const clock = host.clock || SYSTEM_CLOCK;
  const report = progressReporter(host.progress);
  const deadline = deadlineOf(clock.now(), host.expiresAt);
  const finish = (result) => {
    report(null);
    return result;
  };
  let done = {};
  for (;;) {
    if (signal && signal.aborted) return finish(failed(VIEWER_DETAIL.STOPPED));
    const step = nextPrepareStep(viewer.readState(facilityId || null), { facilityId, done, need, paneTarget });
    if (step.kind === PREPARE_STEP.READY) return finish(null);
    if (step.kind === PREPARE_STEP.STOP) return finish(step.result);
    report(step.progress);
    done = { ...done, ...doneAfter(step) };
    const stopped = await act(step, viewer);
    if (stopped) return finish(stopped);
    if (clock.now() >= deadline) return finish(notAvailable(VIEWER_DETAIL.TIMED_OUT));
    await clock.sleep(VIEWER_WAIT.POLL_MS, signal);
  }
}
